import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'

import './plants.css'

type PlantCells = Record<string, string>

type PlantRow = {
  id: string
  cells: PlantCells
}

/**
 * Builds the final URL for plant API calls.
 * - Absolute `REACT_APP_API_PLANT` / remove template → used as-is.
 * - If `REACT_APP_API_BASE_URL` is set (e.g. http://127.0.0.1:5000), relative paths
 *   are joined to it so the browser talks to the backend directly. That avoids
 *   dev-server proxy issues (e.g. 504 when the proxy or IPv6 localhost flakes).
 * - Otherwise same-origin (craco `/api` proxy in dev when base is unset).
 * Backend must send CORS headers for the dev origin when using a direct base URL.
 */
function resolvePlantApiUrl(pathOrUrl: string): string {
  const raw = pathOrUrl.trim()
  if (!raw) return ''
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw
  }
  const baseRaw = process.env.REACT_APP_API_BASE_URL?.trim().replace(/\/$/, '')
  if (
    baseRaw &&
    (baseRaw.startsWith('http://') || baseRaw.startsWith('https://'))
  ) {
    const path = raw.startsWith('/') ? raw : `/${raw}`
    return `${baseRaw}${path}`
  }
  return `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`
}

function buildPlantListRequestUrl(): string {
  const pathOrUrl =
    process.env.REACT_APP_API_PLANT || process.env.REACT_APP_API_DATA || ''
  if (!pathOrUrl) return ''
  return resolvePlantApiUrl(pathOrUrl)
}

function extractPlantJsonList(json: unknown): Record<string, unknown>[] {
  if (Array.isArray(json)) {
    return json.filter(
      (x): x is Record<string, unknown> =>
        x !== null && typeof x === 'object' && !Array.isArray(x)
    )
  }
  if (!json || typeof json !== 'object' || Array.isArray(json)) return []
  const o = json as Record<string, unknown>
  for (const k of ['data', 'plants', 'records', 'items', 'results']) {
    const v = o[k]
    if (Array.isArray(v)) {
      return v.filter(
        (x): x is Record<string, unknown> =>
          x !== null && typeof x === 'object' && !Array.isArray(x)
      )
    }
  }
  return []
}

const SKIP_PLANT_KEYS = new Set(['__v'])

/** Not rendered as columns; still available on records for ids / KPIs where needed. */
const HIDE_PLANT_TABLE_COLUMN_KEYS = new Set([
  'id',
  'pd_id',
  'stp_id',
  'stp_location',
])

function shouldIncludePlantColumnKey(key: string): boolean {
  if (SKIP_PLANT_KEYS.has(key)) return false
  if (key.startsWith('_')) return false
  if (HIDE_PLANT_TABLE_COLUMN_KEYS.has(key)) return false
  return true
}

function derivePlantColumnKeys(records: Record<string, unknown>[]): string[] {
  const seen = new Set<string>()
  const order: string[] = []
  for (const rec of records) {
    for (const k of Object.keys(rec)) {
      if (!shouldIncludePlantColumnKey(k)) continue
      if (seen.has(k)) continue
      seen.add(k)
      order.push(k)
    }
  }
  return order
}

const PLANT_COLUMN_HEADER_LABELS: Record<string, string> = {
  ph_min_value: 'pH(min)',
  ph_max_value: 'pH(max)',
  bod_min_value: 'BOD(min)',
  bod_max_value: 'BOD(max)',
  cod_min_value: 'COD(min)',
  cod_max_value: 'COD(max)',
  tss_min_value: 'TSS(min)',
  tss_max_value: 'TSS(max)',
  flow_mld: 'Flow(ml/d)',
  plant_status: 'Status',
  downtime_hours: 'DownTime(hr)',
  recorded_at: 'Recorded',
}

function plantColumnHeaderLabel(apiKey: string): string {
  return PLANT_COLUMN_HEADER_LABELS[apiKey] ?? apiKey.replace(/_/g, ' ')
}

function formatPlantDateYyyyMmDd(value: unknown): string | null {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : value * 1000
    const d = new Date(ms)
    if (Number.isNaN(d.getTime())) return null
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  const s = String(value).trim()
  if (!s) return null
  const isoPrefix = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (isoPrefix) return `${isoPrefix[1]}-${isoPrefix[2]}-${isoPrefix[3]}`
  const parsed = Date.parse(s)
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed)
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  return null
}

function isLikelyPlantDateColumn(key: string): boolean {
  const k = key.toLowerCase()
  return (
    k.includes('date') ||
    k.endsWith('_at') ||
    k === 'timestamp' ||
    (k.includes('time') && !k.includes('downtime'))
  )
}

function formatPlantScalar(value: unknown, columnKey: string): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  const asDateCell = (): string | null => {
    const ymd = formatPlantDateYyyyMmDd(value)
    return ymd
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    if (isLikelyPlantDateColumn(columnKey)) {
      const ymd = asDateCell()
      if (ymd) return ymd
    }
    return String(value)
  }
  if (typeof value === 'string') {
    const t = value.trim()
    if (!t) return '—'
    if (isLikelyPlantDateColumn(columnKey)) {
      const ymd = asDateCell()
      if (ymd) return ymd
    }
    return t
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '—'
    return JSON.stringify(value)
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>
    if (Object.keys(o).length === 0) return '—'
    return JSON.stringify(value)
  }
  return String(value)
}

function recordToPlantRow(
  rec: Record<string, unknown>,
  cols: string[],
  index: number
): PlantRow {
  const rawId =
    rec.id ?? rec._id ?? rec.plantId ?? rec.plant_id ?? `row-${index}`
  const id = String(rawId)
  const cells: PlantCells = {}
  for (const k of cols) {
    cells[k] = formatPlantScalar(rec[k], k)
  }
  return { id, cells }
}

function plantsGridTemplateColumns(dataColumnCount: number): string {
  if (dataColumnCount <= 0) {
    return `56px minmax(120px, max-content)`
  }
  /* max-content so total width can exceed the card → one shared horizontal scroll */
  return `56px repeat(${dataColumnCount}, minmax(96px, max-content))`
}

function pickFirstStatusColumn(keys: string[]): string | null {
  for (const k of keys) {
    const low = k.toLowerCase()
    if (low === 'status' || low.endsWith('_status') || low === 'state') {
      return k
    }
  }
  return null
}

function countKpiFromRows(
  rows: PlantRow[],
  keys: string[],
  kind: 'green' | 'critical'
): number {
  const statusKey = pickFirstStatusColumn(keys)
  if (!statusKey) return 0
  let n = 0
  for (const r of rows) {
    const s = (r.cells[statusKey] || '').toLowerCase()
    if (kind === 'green') {
      if (
        s.includes('green') ||
        s === 'ok' ||
        s === 'normal' ||
        s === 'on'
      ) {
        n += 1
      }
    } else if (
      s.includes('critical') ||
      s.includes('alert') ||
      s.includes('fail') ||
      (s.includes('down') && !s.includes('downtime')) ||
      s.includes('red') ||
      s === 'off'
    ) {
      n += 1
    }
  }
  return n
}

function isStatusColumnKey(key: string): boolean {
  const low = key.toLowerCase()
  return low === 'status' || low.endsWith('_status') || low === 'state'
}

const Plants = () => {
  const history = useHistory()
  const [plantColumnKeys, setPlantColumnKeys] = useState<string[]>([])
  const [rows, setRows] = useState<PlantRow[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const gridTemplateColumns = useMemo(
    () => plantsGridTemplateColumns(plantColumnKeys.length),
    [plantColumnKeys.length]
  )

  const reloadFromRecords = useCallback((records: Record<string, unknown>[]) => {
    const cols = derivePlantColumnKeys(records)
    setPlantColumnKeys(cols)
    setRows(records.map((rec, idx) => recordToPlantRow(rec, cols, idx)))
  }, [])

  const refreshList = useCallback(async () => {
    const url = buildPlantListRequestUrl()
    if (!url) {
      setListError(
        'REACT_APP_API_PLANT (or fallback REACT_APP_API_DATA) is not defined.'
      )
      return
    }
    setListLoading(true)
    setListError(null)
    try {
      const res = await fetch(url, { method: 'GET' })
      if (!res.ok) {
        throw new Error(`Failed to fetch plants (${res.status})`)
      }
      const json: unknown = await res.json()
      reloadFromRecords(extractPlantJsonList(json))
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to fetch plants.')
    } finally {
      setListLoading(false)
    }
  }, [reloadFromRecords])

  useEffect(() => {
    void refreshList()
  }, [refreshList])

  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      Object.values(r.cells).some((c) => c.toLowerCase().includes(q))
    )
  }, [rows, searchQuery])

  const activeSitesCount = rows.length
  const greenCount = countKpiFromRows(rows, plantColumnKeys, 'green')
  const criticalCount = countKpiFromRows(rows, plantColumnKeys, 'critical')

  const handleSignOut = () => {
    history.push('/')
  }

  const goToContracts = () => {
    history.push('/contatcts')
  }

  const goToPenalty = () => {
    history.push('/penalty')
  }

  const goToPlants = () => {
    history.push('/plants')
  }

  const goToDashboard = () => {
    history.push('/dashboard')
  }

  const goToReports = () => {
    history.push('/reports')
  }

  const goToMonitoring = () => {
    history.push('/monitoring')
  }

  const goToCctv = () => {
    history.push('/cctv')
  }

  const goToInventory = () => {
    history.push('/inventory')
  }

  const goToSettings = () => {
    history.push('/settings')
  }

  return (
    <div className="plants-container1">
      <Helmet>
        <title>Namami Gange</title>
        <meta property="og:title" content="Namami Gange" />
        <link
          rel="icon"
          type="image/png"
          href="/namamigangelogohindi11857-2tv9-200h.png"
        />
        <link
          rel="canonical"
          href="https://namami-ganga-design-file-pvya6a.teleporthq.app/plants"
        />
        <meta
          property="og:url"
          content="https://namami-ganga-design-file-pvya6a.teleporthq.app/plants"
        />
      </Helmet>
      <div className="plants-thq-plants-elm">
        <span className="plants-thq-text-elm10">Plants</span>
        <img
          src="/bg1803-w3o8-300w.png"
          alt="BG1803"
          className="plants-thq-bg-elm"
        />
        <div className="plants-thq-settings-elm1" onClick={goToContracts}>
          <span className="plants-thq-text-elm11">Contracts</span>
        </div>
        <div className="plants-thq-settings-elm2" onClick={goToPenalty}>
          <span className="plants-thq-text-elm12">Penalty</span>
        </div>
        <div className="plants-thq-settings-elm3" onClick={goToReports}>
          <span className="plants-thq-text-elm13">Reports</span>
        </div>
        <div className="plants-thq-settings-elm4" onClick={goToMonitoring}>
          <span className="plants-thq-text-elm14">Real time monitoring</span>
        </div>
        <div className="plants-thq-settings-elm5">
          <span className="plants-thq-text-elm15">Manpower</span>
        </div>
        <div className="plants-thq-settings-elm6" onClick={goToInventory}>
          <span className="plants-thq-text-elm16">Inventory</span>
        </div>
        <span className="plants-thq-text-elm17">Collaboration</span>
        <span
          className="plants-thq-text-elm18"
          onClick={goToCctv}
          role="presentation"
        >
          CCTV Monitoring
        </span>
        <span
          className="plants-thq-text-elm19"
          onClick={goToSettings}
          role="presentation"
        >
          Settings
        </span>
        <div
          className="plants-thq-dashboard-elm"
          onClick={goToDashboard}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') goToDashboard()
          }}
          role="button"
          tabIndex={0}
          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        >
          <span className="plants-thq-text-elm20">Dashboard</span>
          <img
            src="/rectangle2891821-1t7-200h.png"
            alt="Rectangle2891821"
            className="plants-thq-rectangle289-elm"
          />
        </div>
        <img
          src="/grid1822-43cl.svg"
          alt="Grid1822"
          className="plants-thq-grid-elm"
        />
        <img
          src="/settings1823-qz7p.svg"
          alt="Settings1823"
          className="plants-thq-settings-elm7"
        />
        <img
          src="/ellipse37041825-mykl-200h.png"
          alt="Ellipse37041825"
          className="plants-thq-ellipse3704-elm"
        />
        <img
          src="/ellipse37051826-b71q-200h.png"
          alt="Ellipse37051826"
          className="plants-thq-ellipse3705-elm"
        />
        <img
          src="/line4771827-te1.svg"
          alt="Line4771827"
          className="plants-thq-line477-elm"
        />
        <img
          src="/line4781828-xn7.svg"
          alt="Line4781828"
          className="plants-thq-line478-elm"
        />
        <img
          src="/chevron1829-8zyr.svg"
          alt="Chevron1829"
          className="plants-thq-chevron-elm"
        />
        <img
          src="/image1830-kqpd-200h.png"
          alt="Image1830"
          className="plants-thq-image-elm"
        />
        <span className="plants-thq-text-elm21">Rajesh Nair</span>
        <span className="plants-thq-text-elm22">Super Admin</span>
        <img
          src="/arrowexit1833-lple.svg"
          alt="Sign out"
          className="plants-thq-arrow-exit-elm"
          onClick={handleSignOut}
        />
        <img
          src="/alert1834-8qdr.svg"
          alt="Alert1834"
          className="plants-thq-alert-elm"
        />
        <div className="plants-thq-frame99-elm">
          <div className="plants-thq-frame101-elm1">
            <span className="plants-thq-text-elm23">Active Sites</span>
          </div>
          <span className="plants-thq-text-elm24">{activeSitesCount}</span>
        </div>
        <div className="plants-thq-frame100-elm1">
          <div className="plants-thq-frame101-elm2">
            <span className="plants-thq-text-elm25">Green Plants</span>
          </div>
          <span className="plants-thq-text-elm26">{greenCount}</span>
        </div>
        <div className="plants-thq-frame102-elm">
          <div className="plants-thq-frame101-elm3">
            <span className="plants-thq-text-elm27">Critical Alerts</span>
          </div>
          <span className="plants-thq-text-kpi-critical">{criticalCount}</span>
        </div>
        <div className="plants-thq-frame1984080068-elm">
          <div className="plants-thq-frame101-elm4">
            <span className="plants-thq-text-elm28">Total Penalties</span>
          </div>
          <span className="plants-thq-text-elm29">₹50,000</span>
          <img
            src="/rectangle1301855-jhd-200h.png"
            alt="Rectangle1301855"
            className="plants-thq-rectangle130-elm"
          />
          <span className="plants-thq-text-elm30">8 violations</span>
        </div>
        <img
          src="/namamigangelogohindi11857-2tv9-200h.png"
          alt="NamamiGangeLogoHindi11857"
          className="plants-thq-namami-gange-logo-hindi1-elm"
        />
        <span className="plants-thq-text-elm31" onClick={goToPlants}>
          Plants
        </span>
        <div className="plants-thq-frame100-elm2">
          <span className="plants-thq-text-elm43">
            Plants : STP Sites
            <span
              dangerouslySetInnerHTML={{
                __html: ' ',
              }}
            />
          </span>
          <span className="plants-thq-text-elm44">Sites Manager : Plants</span>
          <img
            src="/bounds1880-p2uu.svg"
            alt="Bounds1880"
            className="plants-thq-bounds-elm1"
          />
          <input
            type="search"
            className="plants-live-search-input"
            placeholder="Search plants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search plants"
          />
          <img
            src="/search1882-yjzc.svg"
            alt="Search1882"
            className="plants-thq-search-elm"
          />
          <img
            src="/bounds1883-qshl.svg"
            alt="Bounds1883"
            className="plants-thq-bounds-elm2"
          />
          <span className="plants-thq-text-elm46">Export Market PDF</span>
          <div className="plants-thq-frame2085664894-elm1">
            <div className="plants-table-frame-messages">
              {listError && (
                <div className="plants-submit-error">{listError}</div>
              )}
              {listLoading && (
                <div className="plants-loading-line">Loading plant records...</div>
              )}
              {!listLoading &&
                !listError &&
                plantColumnKeys.length === 0 &&
                rows.length === 0 && (
                  <div className="plants-loading-line">
                    No plant records returned. Columns appear when the API
                    returns objects with fields.
                  </div>
                )}
            </div>
            <div
              className="plants-table-scroll-region"
              role="region"
              aria-label="Plants data table"
            >
              <div className="plants-table-scroll-inner">
                <div className="plants-grid">
                  <div
                    className="plants-grid-head"
                    style={{ gridTemplateColumns }}
                  >
                    <div>S No.</div>
                    {plantColumnKeys.length === 0 ? (
                      <div title="Awaiting API fields">—</div>
                    ) : (
                      plantColumnKeys.map((k) => (
                        <div key={k} title={k}>
                          {plantColumnHeaderLabel(k)}
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className="plants-rows">
                  {visibleRows.map((r, idx) => (
                    <div
                      key={r.id}
                      className="plants-row"
                      style={{ gridTemplateColumns }}
                    >
                      <div className="plants-cell plants-cell-sno">
                        {idx + 1}.
                      </div>
                      {plantColumnKeys.length === 0 ? (
                        <div className="plants-cell">—</div>
                      ) : (
                        plantColumnKeys.map((col) => (
                          <div
                            key={col}
                            className={
                              isStatusColumnKey(col)
                                ? 'plants-cell plants-cell-status'
                                : 'plants-cell'
                            }
                            title={r.cells[col]}
                          >
                            {r.cells[col]}
                          </div>
                        ))
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Plants
